import { Button } from "../UI/button";
import { cn } from "../../Lib/Cn";
import { Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../UI/select";
import { useState, useMemo } from "react"; // Added useMemo
import useIsMobile from "../../Hooks/useIsMobile";
import MobileFullScreenDropdown from "./MobileDropdown";
import { format } from "date-fns";

export function DrawerFilter({
    key,
    id,
    label,
    options = [],
    value,
    onChange,
    displayValue,
    type = "options",
    sortOptionMap = {},
    sortConfig,
    tempsortConfig,
    setTempsortConfig,
    disabled = false,
    mobileDropdownText,
    mobileNoLabel = false,
    tLabel,
    MobileLabel,
    showAllOption = false,
    sortDescending = false,
    isSorting,
    showAllOptionNum = false,
    allText,
    allID,
    prependOnlyName = false,
	searchEnabled = true,
	searchThreshold = 10,
    renderOption,
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const isMobile = useIsMobile();

    const normalizedOptions = useMemo(() => {
        const safeOptions = Array.isArray(options) ? options : [];
        return prependOnlyName
            ? [allText || "All", ...safeOptions]
            : showAllOption
                ? [{ name: allText || "All", ID: allID || "all" }, ...safeOptions]
                : showAllOptionNum
                    ? [{ name: allText || "All", ID: allID || "123456" }, ...safeOptions]
                    : safeOptions;
    }, [options, prependOnlyName, showAllOption, showAllOptionNum, allText, allID]);

    // FIX: Force the order here specifically for the Period filter
    const finalOptions = useMemo(() => {
        if (id === "dateRange" || label === "Period") {
            const preferredOrder = [
                "This Month",
                "Other Month",
                "This Quarter",
                "Other Quarter",
                "This Year",
                "Other Year"
            ];

            // Create a copy and sort based on the preferredOrder index
            return [...normalizedOptions].sort((a, b) => {
                const labelA = typeof a === "object" ? a.name : a;
                const labelB = typeof b === "object" ? b.name : b;

                // Keep "All" at the top if it exists
                if (labelA === "All" || labelA === allText) return -1;
                if (labelB === "All" || labelB === allText) return 1;

                const indexA = preferredOrder.indexOf(labelA);
                const indexB = preferredOrder.indexOf(labelB);

                // If both items are in our preferred list, sort by index
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                // Otherwise fallback to original position
                return 0;
            });
        }
        return normalizedOptions;
    }, [normalizedOptions, id, label, allText]);

    // FIX: Changed from normalizedOptions.filter to finalOptions.filter
    const filteredOptions = (finalOptions || []).filter((option) => {
        const optionText = typeof option === "object" ? option?.name ?? "" : option ?? "";
        return optionText.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });

    const getOptionLabel = (option) => typeof option === "object" ? option.name : option;
    const getOptionValue = (option) => typeof option === "object" ? option.ID || option.value || option.name : option;

    const handleSelect = (option) => {
        const val = getOptionValue(option);
        if (type === "sort" && sortOptionMap[val]) {
            const { key, direction } = sortOptionMap[val];
            if (sortConfig?.key === key && sortConfig?.direction === direction) {
                setTempsortConfig?.({ key: "", direction: "" });
            } else {
                setTempsortConfig?.({ key, direction });
            }
        } else {
            onChange?.(val === value ? "all" : val);
        }
    };

    const handleReset = () => {
        if (type === "sort") setTempsortConfig?.({ key: "", direction: "" });
        else onChange?.("all");
    };

    const getDisplay = () => {
        if (!value || value === "all") return label;
        if (displayValue) return displayValue(value);
        return typeof value === "object" ? value.name : value;
    };

    if (disabled) return null;

    return (
        <div id={id} key={key} className="my-2 rounded-t-[10px]">
            <h3 className={`font-semibold text-md text-text-color mb-3 rounded-t-[10px] ${mobileNoLabel && isMobile ? "hidden" : ""}`}>
                {`${MobileLabel ? MobileLabel : tLabel ? tLabel : label}`}
            </h3>
            {type === "date" && (
                <div className="w-full relative">
                    {isMobile ? (
                        <MobileFullScreenDropdown
                            options={(() => {
                                const today = new Date();
                                const year = today.getFullYear();
                                const month = today.getMonth();
                                const daysInMonth = new Date(year, month + 1, 0).getDate();

                                return Array.from({ length: daysInMonth }, (_, i) => {
                                    const day = i + 1;
                                    const date = new Date(year, month, day);

                                    return {
                                        label: format(date, "dd MMM"), // 👈 no year
                                        value: format(date, "yyyy-MM-dd"),
                                        name: format(date, "dd MMM")
                                    };
                                });
                            })()}
                            value={value || format(new Date(), "yyyy-MM-dd")}
                            onSelect={onChange}
                            onChange={onChange}
                            placeholder="Select Date"
                            displayKey="label"
                            valueKey="value"
                            label={label}
                            hideAllOption
                            showClearButton={false}
                            searchThreshold={5}
                            emptyMessage="No dates found"
                            allowAdd={false}
                            className="w-full"
                            isfilter
                            resetButton={false}
                            mobileDropdownText={mobileDropdownText || "Select Date"}
                            showAllOption={false}
                            isSorting={false}
                        />
                    ) : (
                        <>
                            {/* Visible input (no year) */}
                            <input
                                type="text"
                                readOnly
                                value={value ? format(new Date(value), "dd MMM") : ""}
                                placeholder="Select Date"
                                className="w-full h-11 px-3 py-2 border border-gray-300 sahi-radius text-sm text-gray-800 bg-white shadow-sm cursor-pointer"
                                onClick={(e) => e.currentTarget.nextSibling.showPicker()}
                            />

                            {/* Hidden native date picker */}
                            <input
                                type="date"
                                value={value || ""}
                                onChange={(e) => onChange?.(e.target.value)}
                                className="absolute inset-0 opacity-0 pointer-events-none"
                            />
                        </>
                    )}
                </div>
            )}

            {(type === "options" || type === "sort") && (
                <div className={cn("flex flex-wrap gap-2")}>
                    {filteredOptions.map((option) => {
                        const optionLabel = getOptionLabel(option);
                        const optionValue = getOptionValue(option);
                        const isActive = type === "sort"
                            ? (sortOptionMap[optionValue]?.key === tempsortConfig?.key && sortOptionMap[optionValue]?.direction === tempsortConfig?.direction)
                            : optionValue === value;

                        return (
                            <Button
                                key={optionValue}
                                onClick={() => handleSelect(option)}
                                variant="outlinefiliter"
                                className={cn(
                                    "px-3 py-1 text-xs font-normal sahi-radius border transition-colors duration-200 w-fit",
                                    isActive ? "bg-sahi-primary-color text-white border-sahi-primary-color" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-800"
                                )}
                            >
                                <span className="flex items-center gap-1">
                                    {isActive && <Check className="w-3 h-3" />}
                                    {renderOption ? renderOption(option) : optionLabel}
                                </span>
                            </Button>
                        );
                    })}
                </div>
            )}

            {type === "list" && (
                <div className="w-full max-h-[30vh] space-y-2">
                    {isMobile ? (
                        <MobileFullScreenDropdown
                            // FIX: Passing finalOptions here to ensure correct order in mobile list
                            options={finalOptions.map(option => ({
                                ...option,
                                agencyName: option?.agencyName || option?.AgencyName,
                                label: getOptionLabel(option),
                                value: getOptionValue(option)
                            }))}
                            value={value}
                            onSelect={onChange}
                            onChange={onChange}
                            placeholder={label}
                            displayKey="label"
                            valueKey="value"
                            label={label}
                            displayValue={displayValue}
                            hideAllOption={true}
                            showClearButton={false}
                            searchThreshold={searchThreshold}
                            emptyMessage="No results found"
                            allowAdd={false}
                            className="w-full"
                            isfilter={true}
                            resetButton={true}
                            handleReset={handleReset}
                            mobileDropdownText={mobileDropdownText}
                            showAllOption={false}
                            sortDescending={sortDescending}
                            isSorting={false} // SET TO FALSE to prevent internal re-sorting
                            allText={allText}
                            allID={allID}
                            prependOnlyName={prependOnlyName}
                        />
                    ) : (
                        <Select value={value || "all"} onValueChange={(val) => onChange?.(val)}>
                            <SelectTrigger className="h-11 w-full min-h-[40px] px-2 py-2 border border-gray-300 sahi-radius text-sm text-gray-800 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sahi-primary-color">
                                <SelectValue>{value && value !== "all" ? getDisplay() : label}</SelectValue>
                            </SelectTrigger>
                            <SelectContent position="popper" side="bottom" avoidCollisions={false}>
                                <div className="p-2">
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="w-full sahi-radius border px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-sahi-primary-color"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="max-h-[20vh] overflow-y-auto">
                                    {filteredOptions.map((option) => (
                                        <SelectItem key={getOptionValue(option)} value={getOptionValue(option)}>
                                            {getOptionLabel(option)}
                                            {option?.agencyName && (
                                                <span className="opacity-50 text-xs ml-1.5 font-normal">
                                                    ({option.agencyName})
                                                </span>
                                            )}
                                        </SelectItem>
                                    ))}
                                </div>
                            </SelectContent>
                        </Select>
                    )}
                </div>
            )}
        </div>
    );
}

export default DrawerFilter;;